import React, { ReactNode } from 'react'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { formatDate } from '../../../helpers/date'
import { removeMarkdown, replaceHtml } from '../../../helpers/string'
import { i18nSocietyType } from '../../../i18n'
import { getResourceCostString } from '../../../api/resources'
import { PostText } from '../Post/PostText'
import { PostImportant } from '../Post/PostImportant'
import { eventDateString, eventTimeString } from '../PostEvent/eventDates'
import {
  PostEventCard,
  PostMediaItem,
  PostPollCard,
  PostRow,
  PostRole,
} from '../../../generated/api/post-row'
import { CommentRow } from '../../../generated/api/comment-row'
import BoappaLogo from '../../../assets/icons/boappa.svg'
import '../Post/post.css'
import './postPrint.css'

export const PRINT_SECTIONS = [
  'header',
  'important',
  'author',
  'date',
  'text',
  'media',
  'events',
  'polls',
  'attachments',
  'comments',
] as const

export type PrintSection = (typeof PRINT_SECTIONS)[number]

export type PrintSections = Record<PrintSection, boolean>

export const DEFAULT_PRINT_SECTIONS: PrintSections = {
  header: true,
  important: true,
  author: false,
  date: true,
  text: true,
  media: true,
  events: true,
  polls: true,
  attachments: true,
  comments: false,
}

export const hasPrintSection = (
  row: PostRow,
  section: PrintSection
): boolean => {
  switch (section) {
    case 'important':
      return row.isAlert
    case 'text':
      return row.message.trim().length > 0
    case 'media':
      return row.media.length > 0
    case 'events':
      return row.events.length > 0
    case 'polls':
      return row.polls.length > 0
    case 'attachments':
      return (
        row.offers.length +
          row.resources.length +
          row.contacts.length +
          row.facilities.length >
        0
      )
    default:
      return true
  }
}

interface PostPrintProps {
  row: PostRow
  sections: PrintSections
  // Undefined leaves the comments section out entirely.
  comments?: CommentRow[]
  // Loading or error state shown under the comments, never printed.
  commentsStatus?: ReactNode
}

const useAuthorLabel = (
  societyType?: string
): ((role: PostRole, fullName?: string) => string) => {
  const { translate } = useAppTranslation()
  const context = i18nSocietyType(societyType)
  const adminLabel = translate('society.roles.admin', { context })
  const boardLabel = translate('society.roles.board', { context })

  return (role, fullName) => {
    switch (role) {
      case 'admin':
        return [fullName, adminLabel].filter(Boolean).join(', ')
      case 'board-member':
        return [fullName, boardLabel].filter(Boolean).join(', ')
      case 'admin-society':
        return adminLabel
      case 'board-society':
        return boardLabel
      default:
        return fullName ?? ''
    }
  }
}

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}): ReactNode => (
  <section className="post-print-block flex flex-col gap-2 border-t border-neutral-80 pt-4 first:border-t-0 first:pt-0">
    {title && <h2 className="text-base font-semibold">{title}</h2>}
    {children}
  </section>
)

const PrintMedia = ({ item }: { item: PostMediaItem }): ReactNode => {
  const { translate } = useAppTranslation()

  if (item.kind === 'video') {
    return (
      <p className="post-print-block rounded-md border border-neutral-80 p-4 text-sm italic">
        {translate('postView.print.video')}
      </p>
    )
  }

  return (
    <img
      className="post-print-block max-h-[160mm] w-full rounded-md object-contain"
      src={item.url}
      alt=""
    />
  )
}

const PrintEvent = ({ event }: { event: PostEventCard }): ReactNode => {
  const { translate } = useAppTranslation()

  return (
    <Section>
      <p className="text-lg font-semibold">
        {translate('post.event.prefix')}: {event.title}
        {event.cancelled && (
          <span className="ml-2 rounded border border-black px-1.5 text-sm uppercase">
            {translate('postView.print.cancelled')}
          </span>
        )}
      </p>
      <p>
        {eventDateString(event)},{' '}
        {eventTimeString(event, translate('post.event.allDay'))}
      </p>
      {event.location && <p>{event.location}</p>}
      {event.description && (
        <p className="whitespace-pre-line">{event.description}</p>
      )}
    </Section>
  )
}

const PrintPoll = ({ poll }: { poll: PostPollCard }): ReactNode => {
  const { translate } = useAppTranslation()
  const votes =
    poll.totalVotes === 1
      ? `1 ${translate('poll.votesSingular')}`
      : `${poll.totalVotes} ${translate('poll.votesPlural')}`

  return (
    <Section>
      <p className="text-sm">
        {translate(
          poll.anonymous ? 'poll.anonymousVoting' : 'poll.nonAnonymousVoting'
        )}{' '}
        ({votes})
      </p>
      <p className="text-lg font-semibold">{poll.title}</p>
      <div className="flex flex-col gap-2">
        {poll.options.map((option) => {
          const percentage =
            poll.totalVotes > 0
              ? Math.round((option.votes / poll.totalVotes) * 100)
              : 0
          return (
            <div key={option.id} className="post-print-block">
              <div className="flex justify-between gap-4">
                <span>{option.text}</span>
                <span className="whitespace-nowrap">
                  {option.votes} ({percentage} %)
                </span>
              </div>
              <div className="post-print-bar h-2 w-full rounded bg-neutral-90">
                <div
                  className="h-2 rounded bg-brandGreen"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </Section>
  )
}

export const PostPrint = ({
  row,
  sections,
  comments,
  commentsStatus,
}: PostPrintProps): ReactNode => {
  const { translate } = useAppTranslation()
  const authorLabel = useAuthorLabel(row.society.type)
  const authorLine = authorLabel(row.role, row.author?.fullName)

  return (
    <article className="post-print mx-auto flex max-w-[180mm] flex-col gap-4 bg-white text-black">
      {sections.important && row.isAlert && (
        <div className="post-print-block">
          <PostImportant />
        </div>
      )}

      {sections.header && (
        <header className="flex items-start justify-between gap-4 border-b-2 border-brandGreen pb-3">
          <h1 className="text-2xl font-semibold">{row.society.name}</h1>
          <BoappaLogo className="h-6 w-auto shrink-0" aria-hidden />
        </header>
      )}

      {((sections.author && authorLine) || sections.date) && (
        <div className="flex flex-col gap-1">
          {sections.author && authorLine && (
            <p className="font-semibold">{authorLine}</p>
          )}
          {sections.date && (
            <p className="text-sm">
              {translate('postView.print.published', {
                date: formatDate(row.createdAt),
              })}
            </p>
          )}
        </div>
      )}

      {sections.text && (
        <div className="post-body">
          <PostText
            isAutomaticallyCreatedPost={row.generatedMessage}
            message={row.message}
          />
        </div>
      )}

      {sections.media &&
        row.media.map((item) => <PrintMedia key={item.id} item={item} />)}

      {sections.events &&
        row.events.map((event) => <PrintEvent key={event.id} event={event} />)}

      {sections.polls &&
        row.polls.map((poll) => <PrintPoll key={poll.id} poll={poll} />)}

      {sections.attachments && row.offers.length > 0 && (
        <Section title={translate('post.newLocalOfferText')}>
          {row.offers.map((offer) => (
            <p key={offer.id}>
              {offer.companyName} – {offer.discountHeader}
            </p>
          ))}
        </Section>
      )}

      {sections.attachments && row.resources.length > 0 && (
        <Section title={translate('post.newMarketPlaceItemText')}>
          {row.resources.map((resource) => (
            <div key={resource.id}>
              <p className="font-semibold">{resource.title}</p>
              {resource.description && <p>{resource.description}</p>}
              <p>
                {resource.isFree || resource.cost === '0'
                  ? translate('marketplaceListView.item.free')
                  : getResourceCostString(resource)}
              </p>
            </div>
          ))}
        </Section>
      )}

      {sections.attachments && row.contacts.length > 0 && (
        <Section title={translate('post.newContactText')}>
          {row.contacts.map((contact) => (
            <p key={contact.id}>
              <span className="font-semibold">{contact.name}</span>
              {contact.title && `, ${contact.title}`}
            </p>
          ))}
        </Section>
      )}

      {sections.attachments && row.facilities.length > 0 && (
        <Section title={translate('post.newFacilityText')}>
          {row.facilities.map((facility) => (
            <div key={facility.id}>
              <p className="font-semibold">{facility.name}</p>
              {facility.description && <p>{facility.description}</p>}
            </div>
          ))}
        </Section>
      )}

      {comments && (comments.length > 0 || commentsStatus) && (
        <section className="flex flex-col border-t border-neutral-80 pt-4 first:border-t-0 first:pt-0">
          {comments.length > 0 && (
            <h2 className="mb-2 text-base font-semibold">
              {translate('common.comments')} ({comments.length})
            </h2>
          )}
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="post-print-block flex flex-col gap-1 border-b border-neutral-90 py-3 last:border-b-0"
            >
              {(sections.author || sections.date) && (
                <p className="text-sm">
                  {sections.author && (
                    <span className="font-semibold">
                      {authorLabel(comment.role, comment.author?.fullName)}
                    </span>
                  )}
                  {sections.author && sections.date && ' · '}
                  {sections.date && formatDate(comment.createdAt)}
                </p>
              )}
              <p className="whitespace-pre-line break-words">
                {removeMarkdown(replaceHtml(comment.message))}
              </p>
            </div>
          ))}
          {commentsStatus && (
            <div className="print:hidden">{commentsStatus}</div>
          )}
        </section>
      )}
    </article>
  )
}
